import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Crown,
  ArrowRight,
  CheckCircle,
  Rocket,
  Zap,
  Clock,
  Shield,
  Star,
  X,
  ShoppingBag,
  Gift,
  AlertTriangle,
  Timer,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usePricing } from '../hooks/usePricing';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = targetDate - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex items-center justify-center gap-3">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <div className="bg-black/50 border border-red-500/30 px-3 py-2 min-w-[50px]">
            <span className="text-2xl font-black text-white">{String(value).padStart(2, '0')}</span>
          </div>
          <span className="text-xs text-red-400 uppercase mt-1">{unit}</span>
        </div>
      ))}
    </div>
  );
};

const UpgradePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const exitIntentTriggered = useRef(false);
  const { getPrice, discountPct } = usePricing();
  
  // Set countdown to 48 hours from now (stored in localStorage for persistence)
  const [countdownTarget] = useState(() => {
    const stored = localStorage.getItem('moviebot_upgrade_countdown');
    if (stored) {
      const date = new Date(stored);
      if (date > new Date()) return date;
    }
    const target = new Date(Date.now() + 48 * 60 * 60 * 1000);
    localStorage.setItem('moviebot_upgrade_countdown', target.toISOString());
    return target;
  });

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('moviebot_token');
        const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProject(data);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [projectId]);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentTriggered.current) {
        exitIntentTriggered.current = true;
        setShowExitModal(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOneTimePurchase = async (planId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-onetime-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          project_id: projectId
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Checkout error:', errorData);
        toast.error(errorData.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipForNow = () => {
    setShowExitModal(true);
  };

  const projectTitle = project?.title || 'Your Screenplay';

  return (
    <div className="min-h-screen bg-background text-white overflow-hidden">
      {/* Exit Intent Modal */}
      <AnimatePresence>
        {showExitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-lg w-full bg-gradient-to-b from-red-950/50 to-background border-2 border-red-500/30 p-8"
            >
              <button
                onClick={() => setShowExitModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                
                <h3 className="text-2xl font-black text-white mb-2">Wait! Don&apos;t Leave Empty-Handed</h3>
                <p className="text-muted-foreground mb-6">
                  Special exit offers for &quot;{projectTitle}&quot;
                </p>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* 50% off Basic Script Pass */}
                  <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-lg">
                    <div className="text-emerald-400 text-xs font-bold mb-1">50% OFF</div>
                    <div className="text-white font-bold mb-1">Script Pass</div>
                    <div className="text-2xl font-black text-white mb-2">{getPrice('single_script_discount', 'one_time', '$7.49')}</div>
                    <Button
                      onClick={() => handleOneTimePurchase('single_script_discount')}
                      disabled={loading}
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-2 text-xs"
                      data-testid="exit-discount-btn"
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Get Deal
                    </Button>
                  </div>

                  {/* 25% off Pro Script Pass */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                    <div className="text-yellow-400 text-xs font-bold mb-1">25% OFF PRO</div>
                    <div className="text-white font-bold mb-1">Script Pass Pro</div>
                    <div className="text-2xl font-black text-white mb-2">{getPrice('single_script_pro_discount', 'one_time', '$29.99')}</div>
                    <Button
                      onClick={() => handleOneTimePurchase('single_script_pro_discount')}
                      disabled={loading}
                      className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-2 text-xs"
                      data-testid="exit-discount-pro-btn"
                    >
                      <Crown className="w-3 h-3 mr-1" />
                      Get Pro Deal
                    </Button>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => handleSubscribe('basic_monthly')}
                  disabled={loading}
                  className="w-full border-white/20 text-white hover:bg-white/10 font-bold py-3 mb-3"
                >
                  Or Subscribe from {getPrice('basic_monthly', 'subscriptions', '$3.99')}/month
                </Button>

                <button
                  onClick={() => {
                    setShowExitModal(false);
                    navigate('/dashboard');
                  }}
                  className="text-sm text-muted-foreground hover:text-white transition-colors"
                >
                  No thanks, I&apos;ll stay with the free version
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
        <motion.div 
          className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-20 right-20 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-primary/20 rounded-full mb-6"
          >
            <Rocket className="w-10 h-10 text-primary" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Let&apos;s Finish <span className="text-primary">&quot;{projectTitle}&quot;</span>
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            You&apos;ve laid the groundwork. Your characters are alive. Your story is waiting.
            <span className="text-white font-semibold"> Now let&apos;s write the rest.</span>
          </p>
        </motion.div>

        {/* Countdown Timer Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 p-4 mb-8 text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Timer className="w-5 h-5 text-red-400" />
            <span className="text-red-400 font-bold text-sm uppercase tracking-wider">Introductory Pricing Expires In</span>
          </div>
          <CountdownTimer targetDate={countdownTarget} />
        </motion.div>

        {/* Regional Pricing Banner */}
        {discountPct > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-emerald-500/10 border border-emerald-500/30 p-4 mb-8 flex items-center justify-center gap-3 text-center"
            data-testid="regional-pricing-banner"
          >
            <Globe className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p className="text-emerald-300 text-sm">
              <span className="font-bold">Regional pricing applied</span> — {discountPct}% off for your region
            </p>
          </motion.div>
        )}

        {/* What's Next Preview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-green-500/10 to-primary/10 border border-green-500/30 p-6 mb-10"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-green-400" />
            Here&apos;s What Happens Next
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-black/20 rounded-lg">
              <div className="text-2xl font-black text-primary mb-1">Act 1</div>
              <p className="text-xs text-muted-foreground">Complete the Setup with 6 more powerful beats</p>
            </div>
            <div className="text-center p-4 bg-black/20 rounded-lg">
              <div className="text-2xl font-black text-primary mb-1">Act 2</div>
              <p className="text-xs text-muted-foreground">Build tension with Confrontation & Midpoint</p>
            </div>
            <div className="text-center p-4 bg-black/20 rounded-lg">
              <div className="text-2xl font-black text-primary mb-1">Act 3</div>
              <p className="text-xs text-muted-foreground">Deliver the Resolution & Final Image</p>
            </div>
          </div>
        </motion.div>

        {/* Four Options: Basic, Pro, Script Pass, Script Pass Pro */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
        >
          {/* Basic Plan */}
          <div className="bg-secondary/50 border-2 border-primary p-5 relative">
            <div className="absolute -top-3 left-4">
              <span className="bg-primary text-black text-xs font-bold px-2 py-1">
                POPULAR
              </span>
            </div>
            
            <div className="pt-3">
              <h3 className="text-lg font-bold text-white mb-1">Basic</h3>
              <p className="text-xs text-muted-foreground mb-3">Perfect for completing scripts</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-sm text-muted-foreground line-through">{discountPct > 0 ? '$3.99' : '$5.99'}</span>
                <span className="text-2xl font-black text-white">{getPrice('basic_monthly', 'subscriptions', '$3.99')}</span>
                <span className="text-xs text-muted-foreground">/mo</span>
              </div>

              <ul className="space-y-2 mb-4">
                {[
                  '5 scenes/day',
                  'Full 3-Act structure',
                  'Script export'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                    <span className="text-white">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full bg-primary text-black hover:bg-primary/90 font-bold py-4 text-xs"
                onClick={() => handleSubscribe('basic_monthly')}
                disabled={loading}
                data-testid="upgrade-basic-btn"
              >
                Subscribe
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-5">
            <div className="pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-white">Pro</h3>
                <Crown className="w-4 h-4 text-yellow-500" />
              </div>
              <p className="text-xs text-muted-foreground mb-3">For serious screenwriters</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-sm text-muted-foreground line-through">{discountPct > 0 ? '$9.99' : '$19.99'}</span>
                <span className="text-2xl font-black text-white">{getPrice('pro_monthly', 'subscriptions', '$9.99')}</span>
                <span className="text-xs text-muted-foreground">/mo</span>
              </div>

              <ul className="space-y-2 mb-4">
                {[
                  'Unlimited scenes',
                  'Pro Scene Studio',
                  'Coverage Report'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                    <span className="text-yellow-100">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-bold py-4 text-xs"
                onClick={() => handleSubscribe('pro_monthly')}
                disabled={loading}
                data-testid="upgrade-pro-btn"
              >
                Go Pro
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>

          {/* One-Time Script Pass */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-5 relative">
            <div className="absolute -top-3 left-4">
              <span className="bg-emerald-500 text-black text-xs font-bold px-2 py-1">
                ONE-TIME
              </span>
            </div>
            
            <div className="pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-white">Script Pass</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">This project only</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                {discountPct > 0 && <span className="text-sm text-muted-foreground line-through">$14.99</span>}
                <span className="text-2xl font-black text-white">{getPrice('single_script', 'one_time', '$14.99')}</span>
                <span className="text-xs text-muted-foreground">once</span>
              </div>

              <ul className="space-y-2 mb-4">
                {[
                  'Full 3-Act for this script',
                  'Unlimited scenes',
                  'Never expires'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    <span className="text-white">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold py-4 text-xs"
                onClick={() => handleOneTimePurchase('single_script')}
                disabled={loading}
                data-testid="upgrade-onetime-btn"
              >
                Buy Script
                <ShoppingBag className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>

          {/* Script Pass Pro */}
          <div className="bg-gradient-to-b from-yellow-500/20 to-yellow-500/5 border-2 border-yellow-500 p-5 relative">
            <div className="absolute -top-3 left-4">
              <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-1">
                PRO ONE-TIME
              </span>
            </div>
            
            <div className="pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-white">Script Pass Pro</h3>
                <Crown className="w-4 h-4 text-yellow-500" />
              </div>
              <p className="text-xs text-muted-foreground mb-3">Full pro for this project</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                {discountPct > 0 && <span className="text-sm text-muted-foreground line-through">$39.99</span>}
                <span className="text-2xl font-black text-white">{getPrice('single_script_pro', 'one_time', '$39.99')}</span>
                <span className="text-xs text-muted-foreground">once</span>
              </div>

              <ul className="space-y-2 mb-4">
                {[
                  'All Script Pass features',
                  'Pro Scene Studio',
                  'Coverage Report'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                    <span className="text-yellow-100">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-bold py-4 text-xs"
                onClick={() => handleOneTimePurchase('single_script_pro')}
                disabled={loading}
                data-testid="upgrade-onetime-pro-btn"
              >
                Buy Pro Script
                <Crown className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Trust Signals */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-6 mb-10 text-sm text-muted-foreground"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span>Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span>Instant Access</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span>4.9/5 Writer Rating</span>
          </div>
        </motion.div>

        {/* Urgency/FOMO */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 p-6 text-center mb-8"
        >
          <p className="text-white font-medium">
            <span className="text-red-400 font-bold">90% of aspiring screenwriters</span> never finish their first script.
            <br />
            <span className="text-muted-foreground">You&apos;re already ahead. Don&apos;t stop now.</span>
          </p>
        </motion.div>

        {/* Success Stories */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="grid md:grid-cols-2 gap-6 mb-8"
        >
          <div className="bg-secondary/30 border border-white/10 p-6">
            <div className="flex items-start gap-4">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face"
                alt="Marcus"
                className="w-14 h-14 rounded-full object-cover border-2 border-primary"
              />
              <div>
                <p className="text-muted-foreground italic mb-3">
                  &quot;I started just like you - with an idea and two scenes. Three months later, 
                  my script was optioned by a production company.&quot;
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white">Marcus J.</span>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                    3 Films on Tubi
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-secondary/30 border border-white/10 p-6">
            <div className="flex items-start gap-4">
              <img 
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face"
                alt="Sarah"
                className="w-14 h-14 rounded-full object-cover border-2 border-primary"
              />
              <div>
                <p className="text-muted-foreground italic mb-3">
                  &quot;The Script Pass was perfect for me. I didn&apos;t want a subscription, 
                  just wanted to finish my screenplay. Worth every penny.&quot;
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white">Sarah T.</span>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                    Script Pass User
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Skip for now */}
        <div className="text-center">
          <button 
            onClick={handleSkipForNow}
            className="text-sm text-muted-foreground hover:text-white transition-colors"
            data-testid="skip-upgrade-btn"
          >
            I&apos;ll continue with the free version for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
